import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { Tooltip, Dropdown, Menu, Icon, Popover, Spin, Checkbox } from 'antd';
import Moment from 'react-moment';
import { compare } from 'natural-orderby';
import { customerNeedsRule, CUSTOMER_RULE_HELP } from '~/_domains/tasks/enrollment';

var COL = {
    select: 44, extrato: 96,
    cod: 100, name: 0, email: 200, cnpj: 200, phone: 200,
    companyType: 200, registeredAt: 140, inactivedAt: 140, actions: 100
};

var thBase = {
    padding: '8px 12px', background: '#fafafa',
    borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8',
    borderRight: '1px solid #e8e8e8',
    fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis'
};
var tdBase = {
    padding: '8px 12px', background: '#fff',
    borderBottom: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
};

var stickyLeftTh = { position: 'sticky', left: 0, zIndex: 3, borderLeft: '1px solid #e8e8e8' };
var stickyLeftTd = { position: 'sticky', left: 0, zIndex: 2, borderLeft: '1px solid #e8e8e8' };
var stickyCodTh = { position: 'sticky', left: COL.select, zIndex: 3 };
var stickyCodTd = { position: 'sticky', left: COL.select, zIndex: 2 };
var stickyRightTh = { position: 'sticky', right: 0, zIndex: 3, boxShadow: '-3px 0 6px rgba(0,0,0,0.06)' };
var stickyRightTd = { position: 'sticky', right: 0, zIndex: 2, boxShadow: '-3px 0 6px rgba(0,0,0,0.06)' };

function thS(w, extra) { return Object.assign({}, thBase, w ? { width: w, maxWidth: w } : {}, extra); }
function tdS(w, extra) { return Object.assign({}, tdBase, w ? { width: w, maxWidth: w } : { maxWidth: 0 }, extra); }

var SORTERS = {
    cod: function(a, b) { return compare()(a.cod, b.cod); },
    name: function(a, b) { return (a.name || '').localeCompare(b.name || ''); },
    email: function(a, b) { return (a.email || '').localeCompare(b.email || ''); },
    cnpj: function(a, b) { return (a.identification_number || 0) - (b.identification_number || 0); },
    phone: function(a, b) { return (a.phone || 0) - (b.phone || 0); },
    company_type: function(a, b) { return (a.company_type || '').localeCompare(b.company_type || ''); },
    extrato_bancario: function(a, b) { return (Number(b.extrato_bancario) || 0) - (Number(a.extrato_bancario) || 0); },
    registered_at: function(a, b) { return (a.registered_at || '').localeCompare(b.registered_at || ''); },
    inactived_at: function(a, b) { return (a.inactived_at || '').localeCompare(b.inactived_at || ''); }
};

var CustomersVirtualTable = React.memo(function CustomersVirtualTable(props) {
    var data = props.data;
    var isLoading = props.isLoading;
    var actived = props.actived;
    var styleCompanyName = props.styleCompanyName;
    var onAction = props.onAction;
    var canEditCustomers = props.canEditCustomers;
    var canDeleteCustomers = props.canDeleteCustomers;
    var findUser = props.findUser;
    var filterCompanyType = props.filterCompanyType;
    // Seleção para as ações em massa. O "todos" do cabeçalho pega a lista
    // filtrada inteira, não só as linhas que a tabela virtual desenhou.
    var selected = props.selected || {};
    var onToggleRow = props.onToggleRow;
    var onToggleAll = props.onToggleAll;
    var allSelected = props.allSelected;
    var someSelected = props.someSelected;
    var showExtrato = props.showExtrato;

    var sortState = React.useState('cod');
    var sortField = sortState[0]; var setSortField = sortState[1];
    var orderState = React.useState('ascend');
    var sortOrder = orderState[0]; var setSortOrder = orderState[1];

    var processedData = React.useMemo(function() {
        var src = data || [];
        if (!sortField || !SORTERS[sortField]) return src;
        var result = src.slice().sort(SORTERS[sortField]);
        if (sortOrder === 'descend') result.reverse();
        return result;
    }, [data, sortField, sortOrder]);

    function toggleSort(field) {
        if (sortField === field) {
            if (sortOrder === 'ascend') { setSortOrder('descend'); }
            else { setSortField(null); setSortOrder(null); }
        } else {
            setSortField(field); setSortOrder('ascend');
        }
    }

    function sortIcon(field) {
        if (sortField !== field) return <Icon type="caret-down" style={{ marginLeft: 4, fontSize: 10, opacity: 0.3 }} />;
        return <Icon type={sortOrder === 'ascend' ? 'caret-up' : 'caret-down'} style={{ marginLeft: 4, fontSize: 10, color: '#1890ff' }} />;
    }

    var showInactivedCol = actived === 0;

    // Com largura fixa nas outras colunas, a Empresa ficava só com a sobra e
    // sumia em telas menores: abaixo deste mínimo a tabela rola para o lado.
    var minWidth = COL.select + COL.cod + 260 + COL.email + COL.cnpj + COL.phone + COL.companyType
        + COL.registeredAt + COL.actions + (showExtrato ? COL.extrato : 0) + (showInactivedCol ? COL.inactivedAt : 0);

    var tableComponents = React.useMemo(function() {
        return {
            Table: function(p) {
                return <table {...p} style={Object.assign({}, p.style, {
                    borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: minWidth
                })} />;
            },
            TableRow: function(p) {
                return <tr {...p} className={'row-clabs' + (p.className ? ' ' + p.className : '')} />;
            }
        };
    }, [minWidth]);

    var fixedHeaderContent = React.useCallback(function() {
        return (
            <tr>
                <th style={thS(COL.select, Object.assign({ textAlign: 'center', padding: '8px 0' }, stickyLeftTh))}>
                    <Tooltip title={allSelected ? 'Limpar seleção' : 'Selecionar todos da lista'}>
                        <Checkbox
                            aria-label="Selecionar todos os clientes da lista"
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={function(e) { onToggleAll(e.target.checked); }}
                        />
                    </Tooltip>
                </th>
                <th style={thS(COL.cod, Object.assign({ cursor: 'pointer' }, stickyCodTh))} onClick={function() { toggleSort('cod'); }}>
                    Cod {sortIcon('cod')}
                </th>
                <th style={thS(0, Object.assign({ cursor: 'pointer' }, { position: 'sticky', left: COL.select + COL.cod, zIndex: 3 }))} onClick={function() { toggleSort('name'); }}>
                    Empresa {sortIcon('name')}
                </th>
                {showExtrato && (
                    <th style={thS(COL.extrato, { cursor: 'pointer', textAlign: 'center' })} onClick={function() { toggleSort('extrato_bancario'); }}>
                        <Tooltip title="Aba Extrato Bancário no portal do cliente">Extrato</Tooltip> {sortIcon('extrato_bancario')}
                    </th>
                )}
                <th style={thS(COL.email, { cursor: 'pointer' })} onClick={function() { toggleSort('email'); }}>
                    Email {sortIcon('email')}
                </th>
                <th style={thS(COL.cnpj, { cursor: 'pointer' })} onClick={function() { toggleSort('cnpj'); }}>
                    CNPJ {sortIcon('cnpj')}
                </th>
                <th style={thS(COL.phone, { cursor: 'pointer' })} onClick={function() { toggleSort('phone'); }}>
                    Telefone {sortIcon('phone')}
                </th>
                <th style={thS(COL.companyType, { cursor: 'pointer' })} onClick={function() { toggleSort('company_type'); }}>
                    Atividade {sortIcon('company_type')}
                </th>
                <th style={thS(COL.registeredAt, { cursor: 'pointer' })} onClick={function() { toggleSort('registered_at'); }}>
                    Dt. Entrada {sortIcon('registered_at')}
                </th>
                {showInactivedCol && (
                    <th style={thS(COL.inactivedAt, { cursor: 'pointer' })} onClick={function() { toggleSort('inactived_at'); }}>
                        Inativado Em {sortIcon('inactived_at')}
                    </th>
                )}
                <th style={thS(COL.actions, Object.assign({ textAlign: 'center' }, stickyRightTh))}></th>
            </tr>
        );
    }, [sortField, sortOrder, showInactivedCol, showExtrato, allSelected, someSelected, onToggleAll]);

    function getCompanyTypeLabel(record) {
        if (!filterCompanyType) return record.company_type;
        var found = filterCompanyType.find(function(item) { return item.value === record.company_type_id; });
        return found ? found.text : record.company_type;
    }

    function itemContent(index, record) {
        var isSelected = !!selected[record.id];
        var rowBg = isSelected ? '#F9F0FF' : '#fff';
        return (
            <>
                <td
                    style={tdS(COL.select, Object.assign({ textAlign: 'center', padding: '8px 0', cursor: 'pointer', background: isSelected ? '#F9F0FF' : '#fff' }, stickyLeftTd))}
                    onClick={function() { onToggleRow(record.id); }}
                >
                    <Checkbox
                        aria-label={'Selecionar ' + record.name}
                        checked={isSelected}
                        onClick={function(e) { e.stopPropagation(); }}
                        onChange={function() { onToggleRow(record.id); }}
                    />
                </td>
                <td style={tdS(COL.cod, Object.assign({ background: rowBg }, stickyCodTd))}>{record.cod}</td>
                <td style={tdS(0, { position: 'sticky', left: COL.select + COL.cod, zIndex: 2, background: rowBg })}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <Tooltip title={record.name} placement="topLeft">
                            <span style={Object.assign({ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, styleCompanyName(record))}>
                                {record.name}
                            </span>
                        </Tooltip>
                        {props.onOpenRule && customerNeedsRule(record) && (
                            <Tooltip title={CUSTOMER_RULE_HELP + ' Clique para definir.'} overlayStyle={{ maxWidth: 320 }}>
                                <button
                                    type="button"
                                    data-customer-rule-tag={record.name}
                                    onClick={function(e) { e.stopPropagation(); props.onOpenRule(record); }}
                                    style={{ flexShrink: 0, padding: '0 7px', fontSize: 11, lineHeight: '18px', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', color: '#d46b08', background: '#fff7e6', border: '1px solid #ffd591' }}
                                >
                                    sem regra
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </td>
                {showExtrato && (
                    <td style={tdS(COL.extrato, { textAlign: 'center' })}>
                        {Number(record.extrato_bancario) === 1 ? (
                            <Tooltip
                                title={'Aba ligada' + (record.extrato_bancario_changed_by_name ? ' por ' + record.extrato_bancario_changed_by_name : '')
                                    + (record.extrato_bancario_changed_at ? ' em ' + new Date(String(record.extrato_bancario_changed_at).replace(' ', 'T')).toLocaleDateString('pt-BR') : '')}
                            >
                                <Icon type="check-circle" theme="filled" style={{ color: '#722ed1' }} aria-label="Aba ligada" />
                            </Tooltip>
                        ) : (
                            <span style={{ color: '#d9d9d9' }} aria-label="Aba desligada">—</span>
                        )}
                    </td>
                )}
                <td style={tdS(COL.email)}>
                    <Tooltip title={record.email} placement="topLeft">
                        <span>{record.email}</span>
                    </Tooltip>
                </td>
                <td style={tdS(COL.cnpj)}>{record.cnpj_formated}</td>
                <td style={tdS(COL.phone)}>{record.phone_formated}</td>
                <td style={tdS(COL.companyType)}>
                    <Tooltip title={getCompanyTypeLabel(record)} placement="topLeft">
                        <span>{getCompanyTypeLabel(record)}</span>
                    </Tooltip>
                </td>
                <td style={tdS(COL.registeredAt)}>
                    {record.registered_at && <Moment format="DD/MM/YYYY">{record.registered_at}</Moment>}
                </td>
                {showInactivedCol && (
                    <td style={tdS(COL.inactivedAt)}>
                        {record.inactived_at && (
                            <Tooltip
                                placement="bottom"
                                title={
                                    record.inactived_by
                                        ? 'Inativado por ' + (findUser(record.inactived_by) && findUser(record.inactived_by).name || 'N/A')
                                        : 'Inativado automaticamente'
                                }
                            >
                                <Moment format="DD/MM/YYYY">{record.inactived_at}</Moment>
                            </Tooltip>
                        )}
                    </td>
                )}
                <td style={tdS(COL.actions, Object.assign({ textAlign: 'center' }, stickyRightTd))}>
                    <span onClick={function(e) { e.stopPropagation(); }}>
                        <Dropdown
                            overlayStyle={{ zIndex: 999 }}
                            overlay={
                                <Menu onClick={function(e) { onAction(e, record); }}>
                                    <Menu.Item key="edit" disabled={!canEditCustomers}>
                                        <Icon type="edit" /> Editar
                                    </Menu.Item>
                                    <Menu.Item key="tasks">
                                        <Icon type="check-square" /> Tarefas
                                    </Menu.Item>
                                    {record.inactived_at !== null && (
                                        <Menu.Item key="activate" disabled={!canEditCustomers}>
                                            <Icon type="check" /> Ativar
                                        </Menu.Item>
                                    )}
                                    <Menu.Divider />
                                    {record.inactived_at === null && !record.enabled && (
                                        <Menu.Item key="force_activate" disabled={!canEditCustomers}>
                                            <Icon type="check" /> Forçar Ativação
                                        </Menu.Item>
                                    )}
                                    {record.inactived_at === null && (
                                        <Menu.Item key="inactivate" disabled={!canEditCustomers}>
                                            <Icon type="delete" /> Inativar
                                        </Menu.Item>
                                    )}
                                    {record.inactived_at !== null && (
                                        <Menu.Item key="delete" disabled={!canDeleteCustomers}>
                                            <Icon type="delete" /> Excluir
                                        </Menu.Item>
                                    )}
                                </Menu>
                            }
                            trigger={['click']}
                        >
                            <a className="ant-dropdown-link" href="#">
                                Ações <Icon type="down" />
                            </a>
                        </Dropdown>
                    </span>
                </td>
            </>
        );
    }

    if (!processedData || processedData.length === 0) {
        return (
            <Spin spinning={isLoading}>
                <div style={{ border: '1px solid #e8e8e8', background: '#fff', padding: '40px 20px', textAlign: 'center' }}>
                    <Icon type="inbox" style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
                    <h3 style={{ marginBottom: 8, color: 'rgba(0,0,0,0.85)', fontWeight: 500 }}>Nenhum cliente encontrado</h3>
                    <p style={{ color: 'rgba(0,0,0,0.45)' }}>Não há clientes para exibir com os filtros selecionados.</p>
                </div>
            </Spin>
        );
    }

    return (
        <Spin spinning={isLoading}>
            <TableVirtuoso
                style={{ height: window.innerHeight - 175, border: '1px solid #e8e8e8' }}
                data={processedData}
                components={tableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={itemContent}
                overscan={20}
            />
            <div style={{ padding: '8px 16px', background: '#fafafa', border: '1px solid #e8e8e8', borderTop: 'none', fontSize: 13 }}>
                Total: {processedData.length} cliente(s)
            </div>
        </Spin>
    );
});

export default CustomersVirtualTable;
